import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { appConfigStore } from 'Models/AppConfigStore';
import { store } from 'Models/Store';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { findAllMeasurementsForFerryTrip, getMeasurementValueFromId } from 'Util/_HumanWritten/MeasurementUtils';
import {
	convertWeight,
	EXACT_WEIGHT_MAX_STORABLE,
	parseWeightUnit,
	pickWeightBand,
	roundWeightForUnit,
	toCanonicalKg,
	weightUnitLabel,
} from 'Util/Weight';
import { NumberInput } from 'Views/Components/New/NumberInput';
import { BookingWizardData } from '../../BookingWizardData/BaseBookingWizardData';
import { isVehicleBookingWizardData, VehicleBookingWizardData } from '../../BookingWizardData/VehicleBookingWizardData';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { WizardErrors } from '../../BookingWizardTypes';
import { useVehicleTabContext } from './VehicleTabContext';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';

export function TrailerExactWeightInput() {
	const intl = useIntl();
	const { towOnTypes } = useVehicleTabContext() as any;

	const { wizardData, setWizardData, innerWizardErrors, setInnerWizardErrors } = useBookingWizardDataStore();

	const displayUnit = appConfigStore.weightDisplayUnit;

	const data = wizardData.innerWizardData as VehicleBookingWizardData;
	const towOnType = useMemo(
		() => (towOnTypes ?? []).find((x: any) => x.id === data?.trailerTypeId),
		[towOnTypes, data?.trailerTypeId],
	);

	// TowOnTypeDto on the booking-wizard fetch doesn't include cargo
	// characteristics; pull them from the server-rendered store map instead.
	const characteristicIds = useMemo(
		() => store.towOnTypeCharacteristicsMap?.[data?.trailerTypeId ?? ''] ?? [],
		[data?.trailerTypeId],
	);

	const shouldShow =
		data?.trailerTypeId !== 'NO_TRAILER' && appConfigStore.towOnTypeRequiresExactWeight(characteristicIds);

	// The booking-wizard tow-on type carries only the minimum weight's id (not the measurement
	// value), so resolve the kg value from the measurement store. When the tow-on type has no
	// minimum weight at all, fall back to the smallest weight band in the list so the exact-weight
	// field still pre-fills. (|| not ?? so a 0/not-found result falls through to the next source.)
	const minimumKg =
		towOnType?.minimumWeight?.value ||
		(towOnType?.minimumWeightId ? getMeasurementValueFromId(towOnType.minimumWeightId) : 0) ||
		findAllMeasurementsForFerryTrip('WEIGHT', 'trailer')?.[0]?.value ||
		0;
	const minimumInDisplayUnit = minimumKg > 0 ? convertWeight(minimumKg, 'kg', displayUnit) : 0;
	const maximumKg = appConfigStore.trailerExactWeightMaximumKg;

	useEffect(() => {
		if (!shouldShow) return;
		// Don't overwrite a value the customer has touched.
		if (data.trailerExactWeightUserEdited) return;

		let prefillKg: number | null = null;
		if (isNotNullOrUndefined(towOnType?.exactWeight) && isNotNullOrUndefined(towOnType?.exactWeightUnit)) {
			prefillKg = toCanonicalKg(Number(towOnType.exactWeight), parseWeightUnit(towOnType.exactWeightUnit));
		} else if (minimumKg > 0) {
			prefillKg = minimumKg;
		}
		if (prefillKg === null) return;

		const newData = new BookingWizardData({ ...wizardData });
		(newData.innerWizardData as VehicleBookingWizardData).trailerExactWeight = convertWeight(
			prefillKg,
			'kg',
			displayUnit,
		);
		(newData.innerWizardData as VehicleBookingWizardData).trailerExactWeightUnit = displayUnit;
		const bandId = pickWeightBand(prefillKg, findAllMeasurementsForFerryTrip('WEIGHT', 'trailer') ?? []);
		if (bandId) {
			(newData.innerWizardData as VehicleBookingWizardData).trailerWeightId = bandId;
		}
		setWizardData(newData);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shouldShow, towOnType?.id, displayUnit]);

	if (!isVehicleBookingWizardData(wizardData.innerWizardData) || !shouldShow) {
		return <></>;
	}

	const typedErrors = innerWizardErrors as WizardErrors<VehicleBookingWizardData>;

	const onChangeValue = (value: number | undefined) => {
		const parsed = value ?? null;

		const newData = new BookingWizardData({ ...wizardData });
		const inner = newData.innerWizardData as VehicleBookingWizardData;
		inner.trailerExactWeight = parsed;
		inner.trailerExactWeightUnit = parsed === null ? null : displayUnit;
		inner.trailerExactWeightUserEdited = true;

		// Auto-assign the matching weight band so pricing reflects the entered weight immediately.
		// The server re-derives on save for defence in depth.
		if (parsed !== null && !Number.isNaN(parsed)) {
			const exactKg = toCanonicalKg(parsed, displayUnit);
			const bandId = pickWeightBand(exactKg, findAllMeasurementsForFerryTrip('WEIGHT', 'trailer') ?? []);
			if (bandId) {
				inner.trailerWeightId = bandId;
			}
		}

		setWizardData(newData);

		const newErrors = { ...typedErrors };
		const exactKg = parsed !== null && !Number.isNaN(parsed) ? toCanonicalKg(parsed, displayUnit) : null;
		if (parsed === null || Number.isNaN(parsed)) {
			(newErrors as any).trailerExactWeight = intl.formatMessage({
				id: 'vehicle-tab__trailer-exact-weight__required',
				defaultMessage: 'Weight is required',
			});
		} else if (exactKg !== null && exactKg < minimumKg) {
			(newErrors as any).trailerExactWeight = intl.formatMessage(
				{
					id: 'vehicle-tab__trailer-exact-weight__below-min',
					defaultMessage: 'Must be at least {min} {unit}',
				},
				{ min: roundWeightForUnit(minimumInDisplayUnit, displayUnit), unit: weightUnitLabel(displayUnit) },
			);
		} else if (exactKg !== null && maximumKg !== null && exactKg > maximumKg) {
			const maxInDisplay = convertWeight(maximumKg, 'kg', displayUnit);
			(newErrors as any).trailerExactWeight = intl.formatMessage(
				{
					id: 'vehicle-tab__trailer-exact-weight__above-max',
					defaultMessage: 'Must be no more than {max} {unit}',
				},
				{ max: roundWeightForUnit(maxInDisplay, displayUnit), unit: weightUnitLabel(displayUnit) },
			);
		} else if (parsed > EXACT_WEIGHT_MAX_STORABLE) {
			// Hard ceiling: the value is stored as numeric(10,3) in the entered unit,
			// so anything larger overflows the column and fails the save.
			(newErrors as any).trailerExactWeight = intl.formatMessage(
				{
					id: 'vehicle-tab__trailer-exact-weight__above-storable-max',
					defaultMessage: 'Must be no more than {max} {unit}',
				},
				{ max: EXACT_WEIGHT_MAX_STORABLE, unit: weightUnitLabel(displayUnit) },
			);
		} else {
			delete (newErrors as any).trailerExactWeight;
		}
		setInnerWizardErrors(newErrors);
	};

	const label = intl.formatMessage(
		{
			id: 'vehicle-tab__trailer-exact-weight__label',
			defaultMessage: 'Trailer weight ({unit})',
		},
		{ unit: weightUnitLabel(displayUnit) },
	);

	const currentEnteredKg =
		data.trailerExactWeight != null && !Number.isNaN(data.trailerExactWeight)
			? toCanonicalKg(data.trailerExactWeight, displayUnit)
			: null;
	const isAboveMax = currentEnteredKg !== null && maximumKg !== null && currentEnteredKg > maximumKg;
	const contactMessage = intl
		.formatMessage({
			id: 'weight.exactInput.trailerMaximumExceededContact',
			defaultMessage: 'Please contact us if your trailer exceeds this limit.',
		})
		.trim();
	const aboveMaxError =
		isAboveMax && maximumKg !== null
			? intl
					.formatMessage(
						{
							id: 'vehicle-tab__trailer-exact-weight__above-max',
							defaultMessage: 'Must be no more than {max} {unit}',
						},
						{
							max: roundWeightForUnit(convertWeight(maximumKg, 'kg', displayUnit), displayUnit),
							unit: weightUnitLabel(displayUnit),
						},
					)
					.trim()
			: '';
	const showAboveMaxContact = isAboveMax && aboveMaxError.length > 0 && contactMessage.length > 0;

	// Combined vehicle + trailer cap. When both exact weights are entered and their
	// sum exceeds the configured combined maximum, surface the error inline on this
	// input — mirrored on the vehicle input — instead of letting the server reject
	// the booking with an alert toast. Derived live from the shared wizard data so it
	// clears the moment either weight is lowered. (validateVehicleTab enforces the same
	// cap as a hard navigation gate.)
	const combinedMaxKg = appConfigStore.combinedExactWeightMaximumKg;
	const vehicleEnteredKg =
		data.vehicleExactWeight != null && !Number.isNaN(data.vehicleExactWeight)
			? toCanonicalKg(data.vehicleExactWeight, displayUnit)
			: null;
	const combinedExceeded =
		combinedMaxKg !== null &&
		currentEnteredKg !== null &&
		vehicleEnteredKg !== null &&
		currentEnteredKg + vehicleEnteredKg > combinedMaxKg;
	const combinedError =
		combinedExceeded && combinedMaxKg !== null
			? intl.formatMessage(
					{
						id: 'vehicle-tab__combined-exact-weight__above-max',
						defaultMessage: 'Combined vehicle and trailer weight must be no more than {max} {unit}',
					},
					{
						max: roundWeightForUnit(convertWeight(combinedMaxKg, 'kg', displayUnit), displayUnit),
						unit: weightUnitLabel(displayUnit),
					},
				)
			: undefined;

	return (
		<>
			<NumberInput
				className="trailer-exact-weight"
				label={label}
				placeholder={intl.formatMessage({
					id: 'vehicle-tab__trailer-exact-weight__placeholder',
					defaultMessage: 'e.g. 800',
				})}
				required
				allowNegative={false}
				value={data.trailerExactWeight ?? undefined}
				error={(typedErrors as any)?.trailerExactWeight ?? combinedError}
				onChange={onChangeValue}
			/>
			{showAboveMaxContact && (
				<div className="trailer-exact-weight__above-max-contact">
					<MessageBox status="warning" slim>
						{contactMessage}
					</MessageBox>
				</div>
			)}
		</>
	);
}
